import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  styled,
  useTheme,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  CircularProgress,
  useMediaQuery,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faTimes,
  faStar,
  faRocket,
  faCrown,
  faGem,
  faBolt,
  faBrain,
  faEye,
  faShareAlt,
  faUsers,
  faHeart,
  faChartLine,
  faLightbulb,
  faShieldAlt,
  faClock,
  faArrowRight,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Api from '../../../../../services/api.service'
import { handlingErrors } from '../../../../../utils'
import { useSelector } from 'react-redux'

const SubscriptionContainer = styled(Box)(({ theme }) => ({
  padding: { xs: '60px 0', sm: '80px 0', md: '100px 0' },
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s ease infinite',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '-10%',
    width: '120%',
    height: '600px',
    background:
      'radial-gradient(ellipse at center, rgba(150, 160, 255, 0.03) 0%, transparent 70%)',
    transform: 'translateY(-50%) rotate(-5deg)',
    pointerEvents: 'none',
  },
  '@keyframes gradient': {
    '0%': { backgroundPosition: '0% 50%' },
    '50%': { backgroundPosition: '100% 50%' },
    '100%': { backgroundPosition: '0% 50%' },
  },
}))

const PricingCard = styled(Card)(({ theme, featured, workshop }) => ({
  height: '100%',
  borderRadius: '24px',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  border: featured ? '2px solid transparent' : '1px solid rgba(0, 0, 0, 0.08)',
  background: featured
    ? 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: featured
    ? '0 10px 40px rgba(150, 160, 255, 0.15), 0 4px 20px rgba(0, 0, 0, 0.08)'
    : '0 4px 20px rgba(0, 0, 0, 0.06)',
  '&:hover': {
    transform: 'translateY(-16px) scale(1.02)',
    boxShadow: featured
      ? '0 20px 60px rgba(150, 160, 255, 0.25), 0 8px 30px rgba(0, 0, 0, 0.12)'
      : '0 12px 40px rgba(0, 0, 0, 0.12)',
    borderColor: featured ? 'transparent' : 'rgba(150, 160, 255, 0.3)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: featured ? '8px' : '6px',
    background: workshop
      ? 'linear-gradient(90deg, #7A85F0, #96A0FF, #B4BCFF)'
      : featured
      ? 'linear-gradient(90deg, #7A85F0, #96A0FF, #B4BCFF)'
      : 'linear-gradient(90deg, #9e9e9e, #757575, #bdbdbd)',
  },
  '&::after': {
    content: featured ? '""' : 'none',
    position: 'absolute',
    top: '-50%',
    right: '-50%',
    width: '200%',
    height: '200%',
    background:
      'radial-gradient(circle, rgba(150, 160, 255, 0.05) 0%, transparent 70%)',
    animation: featured ? 'pulse 4s ease-in-out infinite' : 'none',
  },
  '@keyframes pulse': {
    '0%, 100%': { transform: 'scale(1)', opacity: 0.5 },
    '50%': { transform: 'scale(1.1)', opacity: 0.3 },
  },
}))

const PlanIcon = styled(Avatar)(({ theme, color }) => ({
  width: { xs: 60, sm: 70, md: 80 },
  height: { xs: 60, sm: 70, md: 80 },
  backgroundColor: color || '#f5f5f5',
  marginBottom: theme.spacing(3),
  fontSize: { xs: '24px', sm: '28px', md: '32px' },
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  border: '3px solid rgba(255, 255, 255, 0.9)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
  },
}))

const PriceDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'center',
  gap: '12px',
  marginBottom: theme.spacing(4),
  padding: theme.spacing(2),
  borderRadius: '16px',
  background:
    'linear-gradient(135deg, rgba(150, 160, 255, 0.05) 0%, rgba(180, 188, 255, 0.08) 100%)',
  border: '1px solid rgba(150, 160, 255, 0.1)',
}))

const FeatureItem = ({ icon, text, included = true }) => (
  <Box display="flex" alignItems="center" gap={2} mb={2}>
    <FontAwesomeIcon
      icon={included ? faCheck : faTimes}
      style={{
        color: included ? '#4caf50' : '#f44336',
        fontSize: '16px',
        width: '20px',
      }}
    />
    <Typography
      variant="body2"
      color={included ? 'text.primary' : 'text.primary'}
      sx={{
        textDecoration: !included ? 'line-through' : 'none',
        opacity: !included ? 0.6 : 1,
        textAlign: 'left',
      }}
    >
      {text}
    </Typography>
  </Box>
)

const SubscriptionPlansSection = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const user = useSelector((state) => state.userAuth.user)
  const [isOneShot, setIsOneShot] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState('3')
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(false)
  const [quantity, setQuantity] = useState({})

  useEffect(() => {
    getOffers()
  }, [])

  const getOffers = async () => {
    setLoading(true)

    try {
      const result = await Api.siteVitrine.getSubscriptionPlans({
        visible: 1,
        codePays: localStorage.getItem('codePays'),
      })
      const res = handlingErrors(result)

      if (res.status === 401) {
        console.log('erreur')
      }

      if (res.ok) {
        setOffers(res.data)
        setLoading(false)
      }
    } catch (error) {
      console.log(error)
      setLoading(false)
    }
  }

  // Transformation functions to convert API data to component format
  const transformApiDataToMonthlyPlans = (apiData) => {
    const subscriptionPlans = apiData.filter(
      (plan) => plan.type === 'subscription'
    )

    return subscriptionPlans.map((plan) => {
      const iconMap = {
        Essentiel: faBolt,
        Essential: faBolt,
        Boost: faRocket,
        Max: faCrown,
      }

      const iconColorMap = {
        Essentiel: '#96A0FF',
        Essential: '#96A0FF',
        Boost: '#FFB347',
        Max: '#B4BCFF',
      }

      // Extract pricing from pricingTiers for different periods (only for one-shot payments)
      const pricingData = {}

      // Default pricing (monthly rates)
      pricingData.monthly = plan.basePrice
      pricingData['3'] = plan.basePrice * 3
      pricingData['6'] = plan.basePrice * 6
      pricingData['12'] = plan.basePrice * 12

      // Parse pricingTiers array format: [{"months": 3, "price": 783},{"months": 6, "price": 1566},{"months": 12, "price": 3132}]
      if (plan.pricingTiers && plan.pricingTiers.length > 0) {
        //console.log(plan.pricingTiers[0])
        try {
          const parsedPricingData = JSON.parse(plan.pricingTiers[0])

          // Extract pricing for different periods from the specific array format
          if (Array.isArray(parsedPricingData)) {
            parsedPricingData.forEach((tier) => {
              if (tier.months === 3 && tier.price !== 0) {
                pricingData['3'] = tier.price
              } else if (tier.months === 6 && tier.price !== 0) {
                pricingData['6'] = tier.price
              } else if (tier.months === 12 && tier.price !== 0) {
                pricingData['12'] = tier.price
              }
            })
          }
        } catch (e) {
          console.log('Error parsing pricing tiers:', e)
        }
      }

      //console.log(pricingData)

      return {
        id: plan.id,
        name: i18n.language === 'fr' ? plan.name : plan.nameEn || plan.name,
        icon: iconMap[plan.name] || faGem,
        iconColor: iconColorMap[plan.name] || '#66bb6a',
        monthlyPrice: pricingData.monthly,
        threeMonthsPrice: pricingData['3'],
        sixMonthsPrice: pricingData['6'],
        twelveMonthsPrice: pricingData['12'],
        pricingData: pricingData,
        currency:
          JSON.parse(sessionStorage.getItem('currency')).devise || 'CHF',
        period: 'mois',
        features: plan.features.map((feature) =>
          i18n.language === 'fr' ? feature.name : feature.nameEn || feature.name
        ),
        ideal:
          i18n.language === 'fr'
            ? plan.description.replace(/<[^>]*>/g, '')
            : (plan.descriptionEn || plan.description).replace(/<[^>]*>/g, ''),
        featured: plan.name === 'Boost' || plan.nameEn === 'Boost',
      }
    })
  }

  const transformApiDataToOneShotOffers = (apiData) => {
    const oneShotPlans = apiData.filter((plan) => plan.type === 'one_shot')

    return oneShotPlans.map((plan) => {
      return {
        id: plan.id,
        name: i18n.language === 'fr' ? plan.name : plan.nameEn || plan.name,
        duration: `${plan.periodDays}`,
        price: plan.basePrice,
        currency:
          JSON.parse(sessionStorage.getItem('currency')).devise || 'CHF',
        features: plan.features.map((feature) =>
          i18n.language === 'fr' ? feature.name : feature.nameEn || feature.name
        ),
        description: (i18n.language === 'fr'
          ? plan.description
          : plan.descriptionEn || plan.description
        ).replace(/<[^>]*>/g, ''),
        featured: plan.name === 'Boost' || plan.nameEn === 'Boost',
      }
    })
  }

  const transformApiDataToTrialOffer = (apiData) => {
    const trialPlan = apiData.find((plan) => plan.type === 'trial')

    if (!trialPlan) {
      return {
        id: 'trial',
        name: i18n.language === 'fr' ? "Offre d'essai" : 'Trial offer',
        duration: '10',
        price: 0,
        currency:
          JSON.parse(sessionStorage.getItem('currency')).devise || 'CHF',
        features:
          i18n.language === 'fr'
            ? ["Publication de l'annonce uniquement"]
            : ['Publication of the announcement only'],
        restriction:
          i18n.language === 'fr'
            ? 'Hors agences de placement'
            : 'Excluding placement agencies',
        featured: false,
      }
    }

    return {
      id: trialPlan.id,
      name:
        i18n.language === 'fr'
          ? trialPlan.name
          : trialPlan.nameEn || trialPlan.name,
      duration: `${trialPlan.periodDays}`,
      price: trialPlan.basePrice,
      currency: JSON.parse(sessionStorage.getItem('currency')).devise || 'CHF',
      features: trialPlan.features.map((feature) =>
        i18n.language === 'fr' ? feature.name : feature.nameEn || feature.name
      ),
      restriction: (i18n.language === 'fr'
        ? trialPlan.description
        : trialPlan.descriptionEn || trialPlan.description
      ).replace(/<[^>]*>/g, ''),
      featured: false,
    }
  }

  // Get transformed data
  const monthlyPlans = transformApiDataToMonthlyPlans(offers)
  const oneShotOffers = transformApiDataToOneShotOffers(offers)
  const trialOffer = transformApiDataToTrialOffer(offers)

  const workshopBenefits = [
    t('atelierLemon.workshopBenefits.reducedTurnover'),
    t('atelierLemon.workshopBenefits.improvedTeamCohesion'),
    t('atelierLemon.workshopBenefits.increasedEmployeeEngagement'),
    t('atelierLemon.workshopBenefits.reducedStressAndInternalTensions'),
    t('atelierLemon.workshopBenefits.improvedCollectivePerformance'),
  ]

  const getDiscountText = () => {
    return isOneShot ? t('subscriptions.discount10Percent') : ''
  }

  const sizeChip = useMediaQuery(theme.breakpoints.down('sm'))
    ? 'small'
    : 'medium'

  if (loading) {
    return (
      <Container maxWidth={'lg'}>
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      </Container>
    )
  }

  return (
    <Container maxWidth="false" sx={{ px: '0px !important' }}>
      <SubscriptionContainer>
        <Container maxWidth="lg" sx={{ paddingBottom: 6 }} id={'sec1'}>
          {/* Section Header */}
          <Box textAlign="center" pt={6} mb={{ xs: 6, sm: 8, md: 10 }}>
            <Box mb={3}>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                fontWeight="800"
                sx={{
                  color: '#333333',
                  fontSize: {
                    xs: '1rem',
                    sm: '1.5rem',
                    md: '2rem',
                    lg: '2rem',
                  },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                {t('subscriptions.title')}
              </Typography>
              <Box
                sx={{
                  width: { xs: '60px', sm: '70px', md: '80px' },
                  height: '4px',
                  background: '#333333',
                  margin: '16px auto',
                  borderRadius: '2px',
                }}
              />
            </Box>
            <Typography
              variant="h6"
              paragraph
              maxWidth={{ xs: '90%', sm: '80%', md: '700px' }}
              mx="auto"
              sx={{
                fontSize: {
                  xs: '1rem',
                  sm: '1.1rem',
                  md: '1.2rem',
                  lg: '1.3rem',
                },
                lineHeight: 1.6,
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.7)',
                px: { xs: 2, sm: 0 },
              }}
            >
              {t('subscriptions.description')}
            </Typography>

            {/* Payment Type and Period Selector */}
            <Box
              mt={{ xs: 4, sm: 5, md: 6 }}
              sx={{
                p: { xs: 2.5, sm: 3 },
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 249, 250, 0.9))',
                borderRadius: '20px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                maxWidth: { xs: '100%', sm: '700px' },
                mx: 'auto',
              }}
            >
              {/* Payment Type Toggle */}
              <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="center"
                gap={{ xs: 2, sm: 3 }}
                mb={3}
              >
                <Typography
                  variant="body1"
                  color={!isOneShot ? 'primary.main' : 'text.primary'}
                  fontWeight={!isOneShot ? 700 : 500}
                  sx={{
                    transition: 'all 0.3s ease',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    textAlign: 'center',
                  }}
                >
                  {t('subscriptions.monthly')}
                </Typography>

                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isOneShot}
                        onChange={(e) => setIsOneShot(e.target.checked)}
                        color="primary"
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#96A0FF',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                            {
                              backgroundColor: 'rgba(150, 160, 255, 0.2)',
                            },
                        }}
                      />
                    }
                    label=""
                    sx={{ m: 0 }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    color={isOneShot ? 'primary.main' : 'text.primary'}
                    fontWeight={isOneShot ? 700 : 500}
                    sx={{
                      transition: 'all 0.3s ease',
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                      textAlign: 'center',
                    }}
                  >
                    {t('subscriptions.annual')}
                  </Typography>
                  {isOneShot && (
                    <Chip
                      label={getDiscountText()}
                      size="small"
                      color="success"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        fontSize: {
                          xs: '0.7rem',
                          sm: '0.75rem',
                          md: '0.85rem',
                        },
                        height: { xs: '24px', sm: '28px', md: '32px' },
                        px: { xs: 1, sm: 1.5 },
                      }}
                    />
                  )}
                </Box>
              </Box>

              {/* Period Selector */}
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  mb: 2,
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                {t('subscriptions.selectPeriod')}
              </Typography>

              <Grid container spacing={2} justifyContent="center">
                {[
                  {
                    value: '3',
                    label: t('subscriptions.threeMonths'),
                    discount: true,
                  },
                  {
                    value: '6',
                    label: t('subscriptions.sixMonths'),
                    discount: true,
                  },
                  {
                    value: '12',
                    label: t('subscriptions.twelveMonths'),
                    discount: true,
                  },
                ].map((period) => (
                  <Grid item xs={4} sm={4} key={period.value}>
                    <Button
                      variant={
                        selectedPeriod === period.value
                          ? 'contained'
                          : 'outlined'
                      }
                      onClick={() => setSelectedPeriod(period.value)}
                      sx={{
                        py: 1.5,
                        px: { xs: 3, md: 6 },
                        borderRadius: '12px',
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', sm: '0.9rem' },
                        textTransform: 'none',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        ...(selectedPeriod === period.value
                          ? {
                              background:
                                'linear-gradient(135deg, #96A0FF, #B4BCFF)',
                              color: 'white',
                              boxShadow: '0 1px 1px rgba(150, 160, 255, 0.3)',
                              '&:hover': {
                                background:
                                  'linear-gradient(135deg, #96A0FF, #B4BCFF)',
                                boxShadow: '0 1px 1px rgba(150, 160, 255, 0.4)',
                              },
                            }
                          : {
                              borderColor: 'rgba(150, 160, 255, 0.3)',
                              color: 'text.primary',
                              '&:hover': {
                                borderColor: 'rgba(150, 160, 255, 0.5)',
                                backgroundColor: 'rgba(150, 160, 255, 0.04)',
                              },
                            }),
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>{period.label}</Box>
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>

          {/* Monthly/Annual Plans */}
          <Box mb={{ xs: 8, sm: 10, md: 12 }}>
            <Grid container spacing={{ xs: 3, sm: 4 }}>
              {monthlyPlans.map((plan, index) => (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <PricingCard featured={plan.featured}>
                    <CardContent
                      sx={{ p: { xs: 3, sm: 4, md: 5 }, textAlign: 'center' }}
                    >
                      {plan.featured && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 20,
                            right: 20,
                            background:
                              'linear-gradient(135deg, #96A0FF, #B4BCFF)',
                            color: 'white',
                            px: { xs: 2, sm: 3 },
                            py: 1,
                            borderRadius: '25px',
                            fontSize: { xs: '0.75rem', sm: '0.85rem' },
                            fontWeight: 'bold',
                            boxShadow: '0 4px 20px rgba(150, 160, 255, 0.3)',
                          }}
                        >
                          {t('subscriptions.popular')}
                        </Box>
                      )}

                      <PlanIcon color={plan.iconColor}>
                        <FontAwesomeIcon icon={plan.icon} />
                      </PlanIcon>

                      <Typography
                        variant="h4"
                        gutterBottom
                        fontWeight="800"
                        sx={{
                          fontSize: {
                            xs: '1.4rem',
                            sm: '1.6rem',
                            md: '1.8rem',
                            lg: '2.2rem',
                          },
                          color: 'text.primary',
                          mb: 2,
                        }}
                      >
                        {plan.name}
                      </Typography>

                      <PriceDisplay>
                        <Typography
                          variant="h2"
                          fontWeight="900"
                          color="primary.main"
                          sx={{
                            fontSize: {
                              xs: '0.8rem',
                              sm: '1rem',
                              md: '1rem',
                              lg: '1rem',
                            },
                          }}
                        >
                          {plan.currency}{' '}
                          {(() => {
                            if (isOneShot) {
                              // One-shot payment: use pricingData for selected period
                              switch (selectedPeriod) {
                                case '3':
                                  return plan.threeMonthsPrice
                                case '6':
                                  return plan.sixMonthsPrice
                                case '12':
                                  return plan.twelveMonthsPrice
                                default:
                                  return plan.monthlyPrice
                              }
                            } else {
                              // Monthly payment: always show monthly price
                              return plan.monthlyPrice
                            }
                          })()}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                          }}
                        >
                          /
                          {(() => {
                            if (isOneShot) {
                              // One-shot payment: show selected period
                              switch (selectedPeriod) {
                                case '3':
                                  return t('subscriptions.threeMonths')
                                case '6':
                                  return t('subscriptions.sixMonths')
                                case '12':
                                  return t('subscriptions.twelveMonths')
                                default:
                                  return t('subscriptions.threeMonths')
                              }
                            } else {
                              // Monthly payment: always show monthly period
                              return plan.period
                            }
                          })()}
                        </Typography>
                      </PriceDisplay>

                      <Box mb={{ xs: 3, sm: 4 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="600"
                          sx={{
                            color: 'primary.main',
                            mb: 2,
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                          }}
                        >
                          {t('subscriptions.included', 'Annonces incluses')}
                        </Typography>
                        {plan.features.map((feature, idx) => (
                          <FeatureItem key={idx} text={feature} />
                        ))}
                      </Box>

                      <Box
                        mb={{ xs: 3, sm: 4 }}
                        p={{ xs: 2, sm: 3 }}
                        sx={{
                          background:
                            'linear-gradient(135deg, rgba(150, 160, 255, 0.08), rgba(180, 188, 255, 0.12))',
                          borderRadius: '16px',
                          border: '1px solid rgba(150, 160, 255, 0.15)',
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="700"
                          color="primary.main"
                          gutterBottom
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                          }}
                        >
                          {t('subscriptions.idealFor', 'Idéal pour')}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            lineHeight: 1.5,
                            fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          }}
                        >
                          {plan.ideal}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: 'auto', pt: { xs: 2, sm: 3 } }}>
                        <Button
                          variant={plan.featured ? 'contained' : 'outlined'}
                          size="large"
                          fullWidth
                          onClick={() => {
                            if (plan.id !== '1') {
                              const offerWithQuantity = {
                                ...plan,
                                quantity: quantity[plan.id] || 1,
                              }
                              sessionStorage.setItem(
                                'offerSelected',
                                JSON.stringify(offerWithQuantity)
                              )

                              sessionStorage.setItem(
                                'periodeSelected',
                                selectedPeriod
                              )
                              sessionStorage.setItem(
                                'paymentType',
                                isOneShot ? 'one_shot' : 'monthly'
                              )
                              user
                                ? navigate('/entreprises/offre/paiement')
                                : navigate('/auth/inscription/entreprise')
                            } else {
                              navigate('/auth/inscription/entreprise')
                            }
                          }}
                          sx={{
                            borderRadius: '30px',
                            py: { xs: 2.5, sm: 2.5, md: 3 },
                            fontWeight: 'bold',
                            textTransform: 'none',
                            fontSize: {
                              xs: '0.95rem',
                              sm: '1rem',
                              md: '1.1rem',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'visible',
                            zIndex: 1,
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background:
                                'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                              transition: 'left 0.6s',
                              pointerEvents: 'none',
                            },
                            '&:hover::before': {
                              left: '100%',
                            },
                            ...(plan.featured && {
                              background:
                                'linear-gradient(135deg, #96A0FF, #B4BCFF)',
                              boxShadow: '0 4px 20px rgba(150, 160, 255, 0.25)',
                              '&:hover': {
                                background:
                                  'linear-gradient(135deg, #7A85F0, #96A0FF)',
                                transform: 'translateY(-3px) scale(1.02)',
                                boxShadow:
                                  '0 12px 35px rgba(150, 160, 255, 0.4)',
                              },
                              '&:active': {
                                transform: 'translateY(-1px) scale(1.01)',
                              },
                            }),
                            ...(!plan.featured && {
                              borderColor: 'rgba(150, 160, 255, 0.6)',
                              borderWidth: '2px',
                              color: 'primary.main',
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              backdropFilter: 'blur(10px)',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'rgba(150, 160, 255, 0.05)',
                                transform: 'translateY(-3px) scale(1.02)',
                                boxShadow:
                                  '0 8px 25px rgba(150, 160, 255, 0.15)',
                              },
                              '&:active': {
                                transform: 'translateY(-1px) scale(1.01)',
                              },
                            }),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 'inherit',
                                fontSize: 'inherit',
                                color: plan.featured ? 'white' : 'primary.main',
                              }}
                            >
                              {t('subscriptions.choosePlan')}
                            </Typography>
                          </Box>
                        </Button>
                      </Box>
                    </CardContent>
                  </PricingCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* One-Shot Offers */}
          <Box mb={{ xs: 10, sm: 12, md: 16 }}>
            <Box textAlign="center" mb={{ xs: 6, sm: 8 }}>
              <Typography
                variant="h3"
                gutterBottom
                fontWeight="800"
                sx={{
                  fontSize: {
                    xs: '1.6rem',
                    sm: '1.8rem',
                    md: '2.2rem',
                    lg: '2.8rem',
                  },
                  background: 'linear-gradient(135deg, #66bb6a, #4caf50)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('subscriptions.oneShot.title', 'Offres One-Shot')}
              </Typography>
              <Box
                sx={{
                  width: { xs: '50px', sm: '60px' },
                  height: '4px',
                  background: 'linear-gradient(90deg, #66bb6a, #4caf50)',
                  margin: '16px auto',
                  borderRadius: '2px',
                }}
              />
              <Typography
                variant="h6"
                textAlign="center"
                mb={{ xs: 4, sm: 6 }}
                maxWidth={{ xs: '90%', sm: '80%', md: '700px' }}
                mx="auto"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  lineHeight: 1.6,
                  fontWeight: 400,
                  px: { xs: 2, sm: 0 },
                }}
              >
                {t(
                  'subscriptions.oneShot.description',
                  "Paiement unique pour une publication d'annonce"
                )}
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 3, sm: 4 }}>
              {oneShotOffers.map((offer, index) => (
                <Grid item xs={12} sm={6} md={4} key={offer.id}>
                  <PricingCard featured={offer.featured}>
                    <CardContent
                      sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}
                    >
                      <Typography
                        variant="h4"
                        gutterBottom
                        fontWeight="700"
                        sx={{
                          fontSize: {
                            xs: '1.3rem',
                            sm: '1.5rem',
                            md: '1.8rem',
                            lg: '2rem',
                          },
                          mb: 2,
                        }}
                      >
                        {offer.name}
                      </Typography>

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={{ xs: 1, sm: 2 }}
                        mb={3}
                      >
                        <Chip
                          label={
                            offer.duration +
                            (i18n.language === 'fr' ? 'j' : 'd')
                          }
                          size={sizeChip}
                          color="primary"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            height: { xs: '32px', sm: '36px' },
                            px: { xs: 1.5, sm: 2 },
                          }}
                        />
                      </Box>

                      <PriceDisplay>
                        <Typography
                          variant="h3"
                          fontWeight="900"
                          color="primary.main"
                          sx={{
                            fontSize: {
                              xs: '0.8rem',
                              sm: '1rem',
                              md: '1rem',
                              lg: '1rem',
                            },
                          }}
                        >
                          {offer.currency} {offer.price}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                          }}
                        >
                          {t('subscriptions.oneTime')}
                        </Typography>
                      </PriceDisplay>

                      <Box mb={3}>
                        {offer.features.map((feature, idx) => (
                          <FeatureItem key={idx} text={feature} />
                        ))}
                      </Box>

                      <Box
                        p={{ xs: 2, sm: 3 }}
                        sx={{
                          background:
                            'linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(102, 187, 106, 0.12))',
                          borderRadius: '16px',
                          border: '1px solid rgba(76, 175, 80, 0.15)',
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight="700"
                          color="success.main"
                          sx={{
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                          }}
                        >
                          {offer.description}
                        </Typography>
                      </Box>
                      <Button
                        variant={offer.featured ? 'contained' : 'outlined'}
                        size="large"
                        fullWidth
                        onClick={() => {
                          const offerWithQuantity = {
                            ...offer,
                            quantity: 1,
                          }
                          sessionStorage.setItem(
                            'offerSelected',
                            JSON.stringify(offerWithQuantity)
                          )
                          sessionStorage.setItem('periodeSelected', 1)
                          sessionStorage.setItem('paymentType', 'one_shot')
                          user
                            ? navigate('/entreprises/offre/paiement')
                            : navigate('/auth/inscription/entreprise')
                        }}
                        sx={{
                          borderRadius: '30px',
                          py: { xs: 2, sm: 2.5 },
                          fontWeight: 'bold',
                          textTransform: 'none',
                          fontSize: { xs: '0.9rem', sm: '1rem' },
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'visible',
                          zIndex: 1,
                          ...(offer.featured && {
                            background:
                              'linear-gradient(135deg, #4caf50, #66bb6a)',
                            '&:hover': {
                              background:
                                'linear-gradient(135deg, #43a047, #5cb860)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 25px rgba(76, 175, 80, 0.3)',
                            },
                          }),
                          ...(!offer.featured && {
                            borderColor: 'rgba(76, 175, 80, 0.5)',
                            color: 'success.main',
                            '&:hover': {
                              borderColor: 'success.main',
                              backgroundColor: 'rgba(76, 175, 80, 0.04)',
                              transform: 'translateY(-2px)',
                            },
                          }),
                        }}
                      >
                        {t('subscriptions.publish', 'Publier maintenant')}
                      </Button>
                    </CardContent>
                  </PricingCard>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Trial Offer */}
          <Box mb={{ xs: 10, sm: 12, md: 16 }}>
            <Card
              sx={{
                backgroundColor:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 249, 250, 0.95))',
                border: '3px dashed rgba(156, 39, 176, 0.3)',
                borderRadius: '24px',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '6px',
                  background:
                    'linear-gradient(90deg, #9c27b0, #ba68c8, #ce93d8)',
                },
              }}
            >
              <CardContent
                sx={{ p: { xs: 4, sm: 5, md: 6 }, textAlign: 'center' }}
              >
                <Typography
                  variant="h3"
                  gutterBottom
                  fontWeight="800"
                  sx={{
                    fontSize: {
                      xs: '1.6rem',
                      sm: '1.8rem',
                      md: '2.2rem',
                      lg: '2.5rem',
                    },
                    background: 'linear-gradient(135deg, #9c27b0, #ba68c8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                    mb: 2,
                  }}
                >
                  {trialOffer.name}
                </Typography>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={{ xs: 2, sm: 3 }}
                  mb={{ xs: 3, sm: 4 }}
                >
                  <Chip
                    label={
                      trialOffer.duration + (i18n.language === 'fr' ? 'j' : 'd')
                    }
                    size={sizeChip}
                    color="secondary"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      height: { xs: '36px', sm: '40px' },
                      px: { xs: 2, sm: 3 },
                    }}
                  />
                  <Typography
                    variant="h3"
                    fontWeight="900"
                    color="success.main"
                    sx={{
                      fontSize: { xs: '1.6rem', sm: '2rem', md: '2.5rem' },
                    }}
                  >
                    {t('payment.free')}
                  </Typography>
                </Box>
                <Box mb={{ xs: 3, sm: 4 }}>
                  {trialOffer.features.map((feature, idx) => (
                    <FeatureItem key={idx} text={feature} />
                  ))}
                </Box>
                <Typography
                  variant="h6"
                  color="error.main"
                  fontWeight="bold"
                  mb={{ xs: 3, sm: 4 }}
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                  }}
                >
                  {trialOffer.restriction}
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const offerWithQuantity = {
                      ...trialOffer,
                      quantity: quantity[trialOffer.id] || 1,
                    }
                    sessionStorage.setItem(
                      'offerSelected',
                      JSON.stringify(offerWithQuantity)
                    )
                    sessionStorage.setItem('periodeSelected', 10)
                    sessionStorage.setItem('paymentType', 'trial')
                    user
                      ? navigate('/entreprises/offre/paiement')
                      : navigate('/auth/inscription/entreprise')
                  }}
                  sx={{
                    borderRadius: '30px',
                    py: { xs: 2, sm: 2.5, md: 3 },
                    fontWeight: 'bold',
                    textTransform: 'none',
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                    borderColor: 'rgba(156, 39, 176, 0.5)',
                    color: 'secondary.main',
                    px: { xs: 4, sm: 5, md: 6 },
                    '&:hover': {
                      borderColor: 'secondary.main',
                      backgroundColor: 'rgba(156, 39, 176, 0.04)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {t('subscriptions.startTrial', "Commencer l'essai")}
                </Button>
              </CardContent>
            </Card>
          </Box>

          {/* Workshop Section - Les Ateliers du Léman */}
          <Box id={'sec2'}>
            <Card
              sx={{
                background:
                  'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 50%, #e8f5e8 100%)',
                borderRadius: '24px',
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 10px 40px rgba(76, 175, 80, 0.15)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '8px',
                  background:
                    'linear-gradient(90deg, #4caf50, #66bb6a, #81c784, #4caf50)',
                  backgroundSize: '200% 100%',
                  animation: 'gradient 4s ease infinite',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 6, md: 8 } }}>
                <Grid
                  container
                  spacing={{ xs: 6, sm: 8, md: 8 }}
                  alignItems="center"
                >
                  <Grid item xs={12} md={6}>
                    <Box mb={{ xs: 3, sm: 4 }}>
                      <Typography
                        variant="h3"
                        gutterBottom
                        fontWeight="800"
                        sx={{
                          fontSize: {
                            xs: '1.6rem',
                            sm: '1.8rem',
                            md: '2.2rem',
                            lg: '2.8rem',
                          },
                          background:
                            'linear-gradient(135deg, #2e7d32, #4caf50, #66bb6a)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                          mb: 2,
                        }}
                      >
                        {t('atelierLemon.title')}
                      </Typography>
                      <Typography
                        variant="h5"
                        paragraph
                        mb={{ xs: 3, sm: 4 }}
                        sx={{
                          fontSize: {
                            xs: '1.1rem',
                            sm: '1.3rem',
                            md: '1.5rem',
                          },
                          fontWeight: 600,
                          color: 'rgba(46, 125, 50, 0.9)',
                        }}
                      >
                        {t('atelierLemon.subtitle')}
                      </Typography>
                    </Box>

                    <Typography
                      variant="body1"
                      paragraph
                      mb={{ xs: 3, sm: 4 }}
                      sx={{
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                        lineHeight: 1.7,
                        fontWeight: 500,
                        color: 'rgba(0, 0, 0, 0.8)',
                      }}
                    >
                      👉 {t('atelierLemon.description')}
                    </Typography>

                    <Box mb={{ xs: 4, sm: 5 }}>
                      <Box mb={{ xs: 2, sm: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="700"
                          sx={{
                            color: '#ff6f00',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: {
                              xs: '0.95rem',
                              sm: '1rem',
                              md: '1.1rem',
                            },
                            lineHeight: 1.4,
                          }}
                        >
                          🔥 {t('atelierLemon.description2')}
                        </Typography>
                      </Box>
                      <Box mb={{ xs: 2, sm: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="700"
                          sx={{
                            color: '#1976d2',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: {
                              xs: '0.95rem',
                              sm: '1rem',
                              md: '1.1rem',
                            },
                            lineHeight: 1.4,
                          }}
                        >
                          🧠 {t('atelierLemon.description3')}
                        </Typography>
                      </Box>
                      <Box mb={{ xs: 2, sm: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="700"
                          sx={{
                            color: '#ff9800',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: {
                              xs: '0.95rem',
                              sm: '1rem',
                              md: '1.1rem',
                            },
                            lineHeight: 1.4,
                          }}
                        >
                          ⚡ {t('atelierLemon.description4')}
                        </Typography>
                      </Box>
                      <Box mb={{ xs: 2, sm: 3 }}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          fontWeight="700"
                          sx={{
                            color: '#9c27b0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: {
                              xs: '0.95rem',
                              sm: '1rem',
                              md: '1.1rem',
                            },
                            lineHeight: 1.4,
                          }}
                        >
                          🎯 {t('atelierLemon.description5')}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      size="large"
                      endIcon={<FontAwesomeIcon icon={faArrowRight} />}
                      href="https://www.ateliersleman.ch/"
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      sx={{
                        borderRadius: '30px',
                        py: { xs: 2, sm: 2.5, md: 3 },
                        px: { xs: 3, sm: 4, md: 5 },
                        fontWeight: 'bold',
                        textTransform: 'none',
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                        background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                        '&:hover': {
                          color: 'white',
                          background:
                            'linear-gradient(135deg, #43a047, #5cb860)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(76, 175, 80, 0.3)',
                        },
                      }}
                    >
                      {t('subscriptions.workshop.visit')}
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{
                        backgroundColor: 'white',
                        p: { xs: 3, sm: 4 },
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.2)',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background:
                            'linear-gradient(90deg, #4caf50, #66bb6a)',
                          borderRadius: '20px 20px 0 0',
                        },
                      }}
                    >
                      <Typography
                        variant="h5"
                        gutterBottom
                        fontWeight="700"
                        textAlign="center"
                        sx={{
                          fontSize: {
                            xs: '1.2rem',
                            sm: '1.4rem',
                            md: '1.6rem',
                          },
                          color: '#2e7d32',
                          mb: { xs: 3, sm: 4 },
                        }}
                      >
                        {t('atelierLemon.description6')} :
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {workshopBenefits.map((benefit, index) => (
                          <ListItem
                            key={index}
                            sx={{
                              py: { xs: 2, sm: 2.5 },
                              px: 0,
                              borderBottom:
                                index < workshopBenefits.length - 1
                                  ? '1px solid rgba(76, 175, 80, 0.1)'
                                  : 'none',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'rgba(76, 175, 80, 0.04)',
                                transform: 'translateX(8px)',
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{ minWidth: { xs: '35px', sm: '40px' } }}
                            >
                              <Box
                                sx={{
                                  width: { xs: 28, sm: 32 },
                                  height: { xs: 28, sm: 32 },
                                  borderRadius: '50%',
                                  backgroundColor: '#4caf50',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow:
                                    '0 4px 12px rgba(76, 175, 80, 0.3)',
                                }}
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  style={{
                                    color: 'white',
                                    fontSize: { xs: '14px', sm: '16px' },
                                  }}
                                />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={benefit}
                              primaryTypographyProps={{
                                fontWeight: 600,
                                color: 'text.primary',
                                fontSize: {
                                  xs: '0.9rem',
                                  sm: '1rem',
                                  md: '1.05rem',
                                },
                                lineHeight: 1.5,
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </Container>
      </SubscriptionContainer>
    </Container>
  )
}

export default SubscriptionPlansSection
